import type { Permission } from './permissions'

/**
 * Mapa rol → permisos. Fuente única de verdad para autorización en el cliente.
 *
 * Roles definidos por backend:
 *   - ORG_OWNER: propietario único de la organización. Acceso total.
 *   - ORG_MANAGER: gerente operativo. Gestiona negocios y operación.
 *   - ORG_ACCOUNTANT: contador. Sólo lectura de facturación.
 *
 * El onboarding (crear organización/negocio la PRIMERA vez, sin organización)
 * NO se gatea por este mapa: es abierto a cualquier usuario autenticado
 * (política "crear organización es fácil"; se retiró el rol LEAD). Al crear su
 * organización, el usuario recibe ORG_OWNER.
 *
 * Desde el goal portal-roles-1 este mapa SÍ se aplica: el gateway lo espeja
 * server-side (matriz rol→superficie del PortalResolver) y las rutas de
 * cliente van gateadas con <OrgRoleGate> (tres estados: onboarding / "pide un
 * rol al dueño" / página). La pertenencia a secas (organizationId sin rol) no
 * abre datos.
 *
 * Si un rol no está en este mapa, `can()` lo trata como sin permisos.
 */
/**
 * Lo que ve el staff de TecoPOS en Administración, sea `ADMIN` o `EDITOR` de
 * `admin_users`: es lo que portal-server deja hacer a los dos (goal
 * seguridad-remediacion 12, opción A de la coordinadora, 2026-10-19).
 */
const PORTAL_STAFF_PERMISSIONS: readonly Permission[] = [
  'organization:read',
  'businesses:read',
  'billing:read',
  'statistics:read',
  'landing:read',
  'landing:edit',
  'payments:manage',
]

export const ROLE_PERMISSIONS: Record<string, readonly Permission[]> = {
  // PORTAL_ADMIN: staff de TecoPOS (NO es un cliente). Administra el contenido
  // público del portal (landing) y configuración global. Es el ÚNICO rol con
  // permisos `landing:*`. Se mantiene separado de los roles de cliente. Solo
  // él gestiona los administradores del portal.
  PORTAL_ADMIN: [...PORTAL_STAFF_PERMISSIONS, 'admins:manage'],
  // PORTAL_EDITOR: el `EDITOR` de `admin_users` (rol sintético, lo pone
  // `useAuth`). Todo lo del staff MENOS gestionar administradores: si pudiera,
  // se ascendería a ADMIN.
  PORTAL_EDITOR: PORTAL_STAFF_PERMISSIONS,
  ORG_OWNER: [
    'users:list',
    'users:invite',
    'users:update_role',
    'users:deactivate',
    'organization:read',
    'organization:edit',
    'businesses:read',
    'businesses:create',
    'businesses:edit',
    'billing:read',
    // Créditos y recargas: SOLO el dueño (goal portal-roles-1).
    'credits:read',
    'statistics:read',
    // Los tickets de soporte ya no van por permiso: el gating es por ALCANCE
    // (orgTicketScope — goal portal-tickets-alcance-por-membresia).
  ],
  ORG_MANAGER: [
    'organization:read',
    'businesses:read',
    'businesses:create',
    'businesses:edit',
    'billing:read',
    'statistics:read',
  ],
  ORG_ACCOUNTANT: [
    'organization:read',
    'businesses:read',
    'billing:read',
  ],
}
